import { cn } from '@/lib/utils'
import { Icon } from '@/ui/components'
import { type Ref, memo } from 'react'
import { BaseLink } from '..'
import { BaseLinkProps, BaseProps, ChipProps } from '../types'

const Chip = ({
  children,
  href,
  size = 'm',
  variant = 'default',
  iconLeft,
  iconRight,
  className,
  iconClassName,
  ...props
}: ChipProps) => {
  if (!children) {
    return null
  }

  //Composers
  const baseClasses =
    'inline-flex gap-x-2xs items-center relative cursor-pointer leading-none'

  const sizeClasses = {
    xs: 'px-xs py-2xs md:px-sm',
    s: 'py-2xs px-sm',
    m: 'py-2xs px-sm',
    l: 'py-1 px-2',
    shop: 'px-xs py-2xs'
  }

  const variantClasses = {
    default: 'border border-accent-300 bg-accent-300 text-neutral-100',
    outline: 'bg-neutral-200 text-primary-300',
    ghost: 'border border-transparent bg-transparent text-neutral-600',
    text: 'px-0 py-1 border border-transparent bg-transparent text-neutral-600',
    shop: 'bg-shop-100 text-shop-400',
    openingHours: 'bg-hero-alpha text-white',
    topEvent: 'border border-primary-300 bg-primary-300 text-neutral-100',
    fill: 'border border-primary-300 bg-primary-300 text-neutral-100'
  }

  const textClass = cn(
    'relative leading-none',
    size === 'xs' && 'max-md:text-xs! textstyle-sm-button',
    size === 's' && 'textstyle-sm-button',
    size === 'm' && 'textstyle-md-button',
    size === 'l' && 'textstyle-lg-button',
    size === 'shop' && 'textstyle-sm-regular',
    variant === 'outline' && 'top-[-0.05rem]'
  )

  const iconClass = cn(
    'flex items-center justify-center',
    iconClassName,
    size === 'xs' && 'h-3 w-3',
    size === 's' && 'h-3.5 w-3.5',
    size === 'm' && 'h-5 w-5',
    size === 'l' && 'h-5 w-5'
  )

  const classComposer = cn(
    baseClasses,
    sizeClasses[size],
    variantClasses[variant],
    className
  )

  const component = (
    <>
      {iconLeft && <Icon className={iconClass} icon={iconLeft} />}
      <span className={textClass}>{children}</span>
      {iconRight && <Icon className={iconClass} icon={iconRight} />}
    </>
  )

  if (href) {
    const linkProps = props as BaseLinkProps
    return (
      <BaseLink href={href} {...linkProps} className={classComposer}>
        {component}
      </BaseLink>
    )
  }

  const { ref, ...divProps } = props as BaseProps
  return (
    <div
      className={classComposer}
      ref={ref as Ref<HTMLDivElement>}
      {...divProps}
    >
      {component}
    </div>
  )
}

Chip.displayName = 'Chip'

export default memo(Chip)
